import React, { useState, useEffect } from "react";
import Section from "../Section";
import { pageTitle } from "../../helpers/PageTitle";
import BannerSectionStyle3 from "../Section/BannerSection/BannerSectionStyle3";
import AppointmentSection from "../Section/AppointmentSection";
import { Helmet } from "react-helmet";
import { FaStethoscope, FaProcedures, FaHospital, FaUserMd, FaHeart, FaClinicMedical, FaCheckCircle, FaSearchPlus, FaTint, FaHeartBroken, FaDisease, FaBandAid, FaClipboardCheck, FaDna, FaVirus, FaCut, FaNotesMedical, FaHandHoldingMedical, FaVial, FaHistory, FaUndo, FaArrowRight } from "react-icons/fa";

const solutionsData = [
  {
    id: 1,
    category: "female",
    title: "Thin Endometrium",
    content: "A thin endometrium can be caused by hormonal imbalances, insufficient blood flow, or certain medications. Treatments may include hormone therapy (like estrogen), improving blood flow (with medications or lifestyle changes), and using procedures like endometrial scratching.",
    icon: <FaTint />
  },
  {
    id: 2,
    category: "female",
    title: "Fibroid Uterus",
    content: "Fibroids are non-cancerous growths in the uterus that can affect fertility by blocking the fallopian tubes, altering the shape of the uterus, or causing hormonal imbalances. Treatment options include medication, surgery (myomectomy), or minimally invasive procedures like uterine artery embolization.",
    icon: <FaDisease />
  },
  {
    id: 3,
    category: "general",
    title: "Unexplained Infertility",
    content: "Unexplained infertility is diagnosed when no specific cause is found after standard fertility tests. Treatment options may include fertility drugs, intrauterine insemination (IUI), or in vitro fertilization (IVF).",
    icon: <FaSearchPlus />
  },
  {
    id: 4,
    category: "female",
    title: "Polycystic Ovarian Syndrome/Disease (PCOS/PCOD)",
    content: "PCOS/PCOD can cause irregular menstrual cycles, anovulation, and hormonal imbalances, affecting fertility. Treatments include lifestyle changes, medications to induce ovulation (like clomiphene or letrozole), and IVF if necessary.",
    icon: <FaVirus />
  },
  {
    id: 5,
    category: "female",
    title: "Endometriosis",
    content: "Endometriosis, where tissue similar to the uterine lining grows outside the uterus, can cause inflammation and adhesions affecting fertility. Treatments include pain management, hormone therapy, and surgery to remove endometrial tissue. IVF may also be an option.",
    icon: <FaDna />
  },
  {
    id: 6,
    category: "advanced",
    title: "Failed IVF",
    content: "If IVF fails, it is important to consult with your fertility specialist to review the cycle and determine potential reasons for the failure. Possible steps may include changing the IVF protocol, addressing any identified issues, considering additional fertility treatments like ICSI or donor eggs/sperm, and exploring alternative treatments such as surrogacy or adoption.",
    icon: <FaHeartBroken />
  },
  {
    id: 7,
    category: "advanced",
    title: "Failed IUI",
    content: "After a failed IUI cycle, options include repeating the IUI with adjustments (different medications or timing), moving to IVF, or considering additional fertility tests to identify underlying issues.",
    icon: <FaVial />
  },
  {
    id: 8,
    category: "female",
    title: "Repeated Abortion",
    content: "Repeated miscarriages can be caused by genetic factors, hormonal imbalances, uterine abnormalities, or immune system issues. Treatments vary based on the cause and may include hormone therapy, surgical correction of uterine issues, or addressing immune system problems.",
    icon: <FaNotesMedical />
  },
  {
    id: 9,
    category: "female",
    title: "Decreased Ovarian Reserve",
    content: "Decreased ovarian reserve refers to a lower number & quality eggs in the ovaries, often related to age. Management includes using fertility drugs to stimulate the ovaries, IVF with egg retrieval, and considering egg donation.",
    icon: <FaClipboardCheck />
  },
  {
    id: 10,
    category: "general",
    title: "Fertility Preservation",
    content: "Fertility preservation options include egg or sperm freezing, embryo freezing, and ovarian tissue freezing. These methods are often used before medical treatments (like chemotherapy) that might affect fertility or for those wishing to delay childbearing.",
    icon: <FaHospital />
  },
  {
    id: 11,
    category: "male",
    title: "Low Or Nil Sperm Count",
    content: "Low or nil sperm count can be caused by hormonal issues, genetic conditions, lifestyle factors, or medical treatments. Treatments include lifestyle changes, medication, surgery, or assisted reproductive techniques like IUI or IVF with intracytoplasmic sperm injection (ICSI).",
    icon: <FaClinicMedical />
  },
  {
    id: 12,
    category: "advanced",
    title: "Assisted Hatching",
    content: "Assisted hatching is a lab technique used in IVF where the outer shell of the embryo is thinned or opened to help it implant in the uterus. It is often used for patients with repeated IVF failures, older women, or embryos with thick outer shells.",
    icon: <FaCut />
  },
  {
    id: 13,
    category: "male",
    title: "High Sperm DFI",
    content: "High sperm DFI, indicating DNA damage in sperm, can be caused by oxidative stress, infections, lifestyle factors, or age. Treatments include lifestyle changes, antioxidant therapy, and using advanced sperm selection techniques in IVF.",
    icon: <FaSearchPlus />
  }
];

const categoryLabels = {
  female: "Female Care",
  male: "Male Care",
  advanced: "IVF Lab",
  general: "General"
};

const colorOrange = "#FF8B13";

export default function Solution() {
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    pageTitle("Solutions");

    const observerOptions = {
      threshold: 0.05,
      rootMargin: '0px 0px -30px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('cs_active');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.cs_reveal').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [activeTab]);

  const filteredSolutions = activeTab === "all"
    ? solutionsData
    : solutionsData.filter(item => item.category === activeTab);

  return (
    <>
      <Helmet>
        <title>Fertility Solutions – Pluro Jabalpur Fertility Centre</title>
        <meta name="description" content="Explore advanced solutions for thin endometrium, PCOS, endometriosis, failed IVF/IUI, and male infertility challenges." />
        <meta name="keywords" content="infertility solution, pcos treatment, failed ivf support, endometriosis treatment, low sperm count solution" />
        <link rel="canonical" href="https://jabalpurivfcentre.com/Solution" />
      </Helmet>

      {/* Banner */ }
      <BannerSectionStyle3
        bgUrl="/images/Solution.jpg"
        title="Welcome to <br /> Pluro Jabalpur<br /> Fertility Center"
        subTitle="Are You Ready to Explore Solutions ?"
      />

      {/* Solutions Directory Section */ }
      <section className="cs_section_light_bg">
        <div className="container">
          <div className="text-center mb-5 cs_reveal">
            <p style={ { textTransform: 'uppercase', letterSpacing: '2px', color: '#be703f', fontWeight: '700', marginBottom: '10px' } }>YOU WANT</p>
            <h2 className="cs_fs_40 cs_medium" style={ { fontFamily: '"Playfair Display", serif', color: '#1c2b38', marginBottom: '15px' } }>SOLUTION FOR</h2>
            <div style={ { width: '60px', height: '3px', backgroundColor: '#9E58B6', margin: '0 auto 25px' } }></div>
          </div>

          {/* Filter Tabs - Capsule style matching achievements */ }
          <div className="cs_filter_tabs cs_reveal">
            <button
              className={ `cs_filter_btn ${activeTab === "all" ? "active" : ""}` }
              onClick={ () => setActiveTab("all") }
            >
              <FaClipboardCheck className="tab_icon" />
              <span className="tab_text">All Conditions</span>
            </button>
            <button
              className={ `cs_filter_btn ${activeTab === "female" ? "active" : ""}` }
              onClick={ () => setActiveTab("female") }
            >
              <FaHeart className="tab_icon" />
              <span className="tab_text">Female Infertility</span>
            </button>
            <button
              className={ `cs_filter_btn ${activeTab === "male" ? "active" : ""}` }
              onClick={ () => setActiveTab("male") }
            >
              <FaClinicMedical className="tab_icon" />
              <span className="tab_text">Male Infertility</span>
            </button>
            <button
              className={ `cs_filter_btn ${activeTab === "advanced" ? "active" : ""}` }
              onClick={ () => setActiveTab("advanced") }
            >
              <FaVial className="tab_icon" />
              <span className="tab_text">Advanced IVF / Lab</span>
            </button>
            <button
              className={ `cs_filter_btn ${activeTab === "general" ? "active" : ""}` }
              onClick={ () => setActiveTab("general") }
            >
              <FaHospital className="tab_icon" />
              <span className="tab_text">General Solutions</span>
            </button>
          </div>

          {/* Cards Grid */ }
          <div className="row cs_gap_y_40">
            { filteredSolutions.map((item) => (
              <div className="col-lg-4 col-md-6" key={ item.id }>
                <div className={ `cs_ultra_card cs_reveal ${item.category === "female" || item.category === "general" ? "theme-purple" : "theme-orange"}` }>

                  {/* Category Badge */ }
                  <div className="card_badge">
                    { categoryLabels[item.category] || "Solution" }
                  </div>

                  {/* Icon Container (Squircle) */ }
                  <div className="icon_squircle">
                    { item.icon }
                  </div>

                  <h3>{ item.title }</h3>
                  <p>{ item.content }</p>
                </div>
              </div>
            )) }
          </div>
        </div>
      </section>

      {/* Appointment Section */ }
      <Section
        topMd={ 120 }
        topLg={ 80 }
        topXl={ 60 }
        bottomMd={ 120 }
        bottomLg={ 80 }
        bottomXl={ 60 }
        id="appointment"
      >
        <AppointmentSection
          sectionTitle="Appointment"
          sectionTitleUp="BOOK AN"
          imgUrl="/images/home_1/appointment1.JPG"
        />
      </Section>
    </>
  );
}
